import {LocalAudioTrack, LocalVideoTrack} from 'twilio-video';
import throttle = require('lodash/throttle');
import {
  STATUS_NO_ACCESS,
  STATUS_NO_ACCESS_CAM,
  STATUS_NO_ACCESS_MIC,
  STATUS_CONNECTED,
  STATUS_DISCONNECTED,
  STATUS_CONNECTION_FAILED,
  STATUS_UNREGISTERED,
  STATUS_OK,
} from '../../redux/modules/video/constants';
import {StatusColor} from './status-bar';
import {SessionType} from '../../redux/modules/session-router/reducer';

export interface IStatusMessage {
  show: boolean;
  loader: boolean;
  videoVisible: boolean;
  title: string;
  message: string;
}

const MAX_MIC_LEVEL = 200;

export function audioEnabled(participant: any): boolean {
  for (let track of participant.audioTracks.values()) {
    if (track.isTrackEnabled) {
      return true;
    }
  }
  return false;
}

export function videoEnabled(participant: any): boolean {
  for (let track of participant.videoTracks.values()) {
    if (track.isTrackEnabled) {
      return true;
    }
  }
  return false;
}

export function enableAudio(participant: any, enabled: boolean): void {
  participant.audioTracks.forEach(trackPub => {
    const {track} = trackPub;
    LocalAudioTrack.prototype.enable.call(track, enabled);
  });
}

export function enableVideo(participant: any, enabled: boolean): void {
  participant.videoTracks.forEach(trackPub => {
    const {track} = trackPub;
    LocalVideoTrack.prototype.enable.call(track, enabled);
  });
}

export function detachTracks(participant: any): void {
  participant.tracks.forEach(trackPub => {
    const {track} = trackPub;
    track.detach().forEach((element: HTMLElement) => {
      element.remove();
    });
  });
}

export function assignMicActivityLevel(
    context: AudioContext,
    track: any,
    element: HTMLElement,
    cssWave: string,
): void {
  const analyser = context.createAnalyser();
  const stream = new MediaStream([track.mediaStreamTrack]);
  const microphone = context.createMediaStreamSource(stream as MediaStream);
  const processor = context.createScriptProcessor(2048, 1, 1);

  analyser.smoothingTimeConstant = 0.3;
  analyser.fftSize = 1024;

  microphone.connect(analyser);
  analyser.connect(processor);
  processor.connect(context.destination);

  // add wave once every 200ms
  const handler = throttle(() => {
    // element can be empty when component is going to be destroyed
    // in this case there is no reason to show wave
    if (!element) {
      return;
    }

    const array = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(array);
    const micLevel = array.reduce((a, b) => a + b) / array.length;
    const wave = createWave(micLevel / MAX_MIC_LEVEL, cssWave);
    element.appendChild(wave);

    // remove wave after 1s
    setTimeout(() => element.removeChild(wave), 1000);
  }, 200);

  processor.addEventListener('audioprocess', handler);
  track.mediaStreamTrack.addEventListener('ended', () => {
    processor.removeEventListener('audioprocess', handler);
  });
}

function createWave(level: number, cssWave: string): HTMLElement {
  const wave: any = document.createElement('div');
  wave.className = cssWave;
  wave.style.opacity = level;
  return wave;
}

/* tslint:disable:no-switch-case-fall-through */
export const statusToColor = (status: string): StatusColor => {
  switch (status) {
    case STATUS_OK: return 'green';

    case STATUS_CONNECTED:
    case STATUS_NO_ACCESS:
    case STATUS_NO_ACCESS_CAM:
    case STATUS_NO_ACCESS_MIC: return 'yellow';

    case STATUS_DISCONNECTED:
    case STATUS_UNREGISTERED:
    case STATUS_CONNECTION_FAILED:
    default: return 'grey';
  }
};

export const selfStatusToMessage = (status: string): IStatusMessage => {
  switch (status) {
    case STATUS_NO_ACCESS:
    case STATUS_NO_ACCESS_CAM:
    case STATUS_NO_ACCESS_MIC: return {
      show: true,
      loader: false,
      videoVisible: true,
      title: 'We need access to your camera and microphone to get started',
      message: 'Click the camera icon in the address bar ' +
      'to give Pramp access to your computer\'s camera and\u00a0microphone.',
    };
    case STATUS_CONNECTION_FAILED: return {
      show: true,
      loader: false,
      videoVisible: false,
      title: 'We seem to have a problem with your connection',
      message: 'Please try and refresh your browser. If that doesn\'t help, ' +
               'please close all open browser windows and return to this page via the link on\u00a0your\u00a0dashboard',
    };
    case STATUS_CONNECTED: return {
      show: true,
      loader: true,
      videoVisible: false,
      title: 'Connecting to server...',
      message: '',
    };
    case STATUS_DISCONNECTED: return {
      show: true,
      loader: true,
      videoVisible: true,
      title: 'Reconnecting...',
      message: '',
    };
    case STATUS_OK:
    default: return {
      show: false,
      loader: false,
      videoVisible: true,
      title: '',
      message: '',
    };
  }
};

export const peerStatusToMessage = (
  status: string,
  peerStatusChangedAt: Date,
  startTime: Date,
  isInterviewer: boolean,
  sessionType: SessionType,
): IStatusMessage => {
  const now = Date.now();
  const peer = 'peer';

  switch (status) {
    case STATUS_NO_ACCESS:
    case STATUS_NO_ACCESS_CAM:
    case STATUS_NO_ACCESS_MIC: return {
      show: true,
      loader: false,
      videoVisible: true,
      title: `Your ${peer} has joined`,
      message: (
        'Please wait while they\'re configuring their video & audio settings. ' +
        'If it takes more than few minutes, consider ending the\u00a0interview.'
      ),
    };
    case STATUS_CONNECTION_FAILED: return {
      show: true,
      loader: false,
      videoVisible: true,
      title: `Your ${peer} is having connection problems`,
      message: 'We\'re helping them resolve the issue, please wait a minute while we get everything worked\u00a0out',
    };
    case STATUS_CONNECTED: return {
      show: true,
      loader: true,
      videoVisible: true,
      title: `Connecting your ${peer}...`,
      message: '',
    };
    case STATUS_UNREGISTERED: return {
      videoVisible: true,
      loader: false,
      show: true,
      message: 'Waiting for your peer to join',
      title: 'Invite Sent',
    };
    case STATUS_DISCONNECTED:
      const peerStatusHasNotChangedDuring5seconds = peerStatusChangedAt && now - peerStatusChangedAt.getTime() >= 5 * 1000;
      const interviewLastsMoreThan45minutes = startTime && now - startTime.getTime() >= 45 * 60 * 1000;

      if (peerStatusHasNotChangedDuring5seconds) {
        const statusMessage: IStatusMessage = {
          show: true,
          loader: false,
          videoVisible: true,
          title: `Your ${peer} seems to be offline`,
          message: '',
        };

        if (interviewLastsMoreThan45minutes) {
          return {
            ...statusMessage,
            message: 'If the interview is over, go ahead and click the "End Session" button. ' +
            'Otherwise, let\'s cross our fingers and wait for your peer\u00a0to\u00a0return.',
          };
        } else {
          return {
            ...statusMessage,
            message: 'Please sit back and wait until they reconnect. ' +
            'If you feel like they\'re not coming back, go ahead and click the "End\u00a0Interview"\u00a0button.',
          };
        }
      } else {
        return {
          show: true,
          loader: true,
          videoVisible: true,
          title: `Reconnecting your ${peer}...`,
          message: '',
        };
      }
    case STATUS_OK:
    default: return {
      show: false,
      loader: false,
      videoVisible: true,
      title: '',
      message: '',
    };
  }
};
/* tslint:enable:no-switch-case-fall-through */
